import { LifeBuoy, Star } from "lucide-react";
import { cn } from "@wa/ui";
import type { MailThread } from "../../../lib/mail";
import type { CrmLabel } from "../../../lib/crmLabels";
import { Avatar, LabelChips, fmtStamp } from "../../ui";
import type { RowMenuProps } from "../../shell/ContextMenu";
import { useWorkCompact } from "../_shared/density";
import { threadWho } from "./ThreadRowItems";

/**
 * One conversation in the middle pane.
 *
 * Its own file because it is the one place where the two densities are not the
 * same markup with different numbers. Roomy, the row is four stacked lines -
 * sender, subject, preview, labels - and the preview is what makes a morning's
 * triage possible without opening anything. Compact, those four lines become
 * two: the preview moves up onto the subject's line behind a middle dot, the way
 * every mail client that has ever offered a dense mode does it.
 *
 * That is the whole trick of the compact view. Halving paddings only ever buys
 * back a few pixels a row and makes the screen feel cheap; taking a line out of
 * the stack buys back a third of the row and leaves every word that was there.
 *
 * `inGroup` is the third shape, and it is a swap rather than a redesign: inside
 * a folded group the headline becomes the SUBJECT, because the sender is what
 * the line above already says and printing it again on all four rows spends the
 * most valuable line in the row on a word the operator has just read. Every
 * other part stays exactly where it was, so the row is still recognisably the
 * one the flat folder draws.
 */
export function ThreadRow({
  thread,
  open,
  labels,
  campaign,
  onOpen,
  menuProps,
  inGroup = false,
  mailbox,
}: {
  thread: MailThread;
  open: boolean;
  labels: CrmLabel[];
  campaign: string | undefined;
  onOpen: (id: string) => void;
  /** Right-click and long-press, from the list's one shared menu. */
  menuProps: RowMenuProps;
  /** Drawn under a folded group line, which already names the correspondent. */
  inGroup?: boolean;
  /** Whose mailbox the conversation is in, in the combined inbox. */
  mailbox?: string;
}) {
  const compact = useWorkCompact();
  const last = thread.messages?.[0];
  const who = threadWho(thread);
  const snippet = last?.snippet;

  // Drawn once and placed by whichever branch runs: the marks belong to the
  // subject line in both densities, and duplicating them was how they drifted.
  const marks = (
    <>
      {thread.starred ? <Star className="mr-1 inline size-3 fill-amber-400 align-[-1px] text-amber-400" /> : null}
      {thread.ticketId ? <LifeBuoy className="mr-1 inline size-3 align-[-1px] text-violet-500" /> : null}
    </>
  );

  return (
    <button
      type="button"
      className={cn("crm-item", open && "is-on", thread.unread && "is-unread")}
      onClick={() => onOpen(thread.id)}
      {...menuProps}
    >
      <Avatar name={who} size={compact ? 28 : 38} />
      <span className="min-w-0 flex-1">
        <span className="crm-item-top">
          <span className="crm-item-from">
            {inGroup ? marks : null}
            {inGroup ? thread.subject || "-" : who}
            {/* Compact keeps its promise inside a group too: the preview rides
                the headline behind the same middle dot the flat compact row
                uses, so a group child is one line there and two when roomy. */}
            {inGroup && compact && snippet ? <span className="crm-item-tail">{snippet}</span> : null}
          </span>
          <span className="crm-item-time">{fmtStamp(thread.lastMessageAt)}</span>
        </span>

        {inGroup ? (
          // The subject moved up, so the only line left is the preview, and
          // compact has already borrowed it onto the headline.
          !compact && snippet ? <span className="crm-item-pre block">{snippet}</span> : null
        ) : compact ? (
          <span className="crm-item-sub block">
            {marks}
            {thread.subject || "-"}
            {snippet ? <span className="crm-item-tail">{snippet}</span> : null}
          </span>
        ) : (
          <>
            <span className="crm-item-sub block">
              {marks}
              {thread.subject || "-"}
            </span>
            {snippet ? <span className="crm-item-pre block">{snippet}</span> : null}
          </>
        )}

        <LabelChips labels={labels} tags={thread.tags} className={compact ? "mt-1" : "mt-[7px]"} />
        {mailbox ? <span className={cn("crm-chip mr-1", compact ? "mt-1" : "mt-[7px]")}>{mailbox}</span> : null}
        {campaign ? <span className={cn("crm-chip", compact ? "mt-1" : "mt-[7px]")}>{campaign}</span> : null}
      </span>
    </button>
  );
}
